/**
 * Hooks TanStack Query para a área de billing admin.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../shared/data/queryKeys';
import {
  changePlanPrice,
  createModelPrice,
  createPlan,
  deletePlan,
  getBillingConfig,
  listModelPrices,
  listMultipliers,
  listPlans,
  provisionPlan,
  updateBillingConfig,
  updateMultiplier,
  updatePlan,
} from './api';
import type {
  BillingConfigUpdate,
  ModelPriceCreate,
  MultiplierUpdate,
  PlanChangePrice,
  PlanCreate,
  PlanProvision,
  PlanUpdate,
} from './types';

/** Configuração global de billing. */
export function useBillingConfig() {
  return useQuery({
    queryKey: queryKeys.billingConfig,
    queryFn: getBillingConfig,
  });
}

/** Mutação: atualizar configuração global. */
export function useUpdateBillingConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BillingConfigUpdate) => updateBillingConfig(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingConfig });
    },
  });
}

/** Lista de multiplicadores por source. */
export function useMultipliers() {
  return useQuery({
    queryKey: queryKeys.billingMultipliers,
    queryFn: listMultipliers,
  });
}

/** Mutação: atualizar um multiplicador específico. */
export function useUpdateMultiplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ source, payload }: { source: string; payload: MultiplierUpdate }) =>
      updateMultiplier(source, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingMultipliers });
    },
  });
}

/** Lista de planos do catálogo. */
export function usePlans() {
  return useQuery({
    queryKey: queryKeys.billingPlans,
    queryFn: listPlans,
  });
}

/** Mutação: criar plano. */
export function useCreatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlanCreate) => createPlan(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

/** Mutação: atualizar plano. */
export function useUpdatePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, payload }: { key: string; payload: PlanUpdate }) =>
      updatePlan(key, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

/** Mutação: deletar plano. */
export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => deletePlan(key),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

/** Mutação: provisionar plano novo (cria Product+Price no Stripe). */
export function useProvisionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PlanProvision) => provisionPlan(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

/** Mutação: mudar preço de um plano (cria Price novo e migra assinantes). */
export function useChangePlanPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, payload }: { key: string; payload: PlanChangePrice }) =>
      changePlanPrice(key, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingPlans });
    },
  });
}

/** Lista de preços de modelos de IA. */
export function useModelPrices() {
  return useQuery({
    queryKey: queryKeys.billingPrices,
    queryFn: listModelPrices,
  });
}

/** Mutação: registrar novo preço de modelo (append-only). */
export function useCreateModelPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ModelPriceCreate) => createModelPrice(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.billingPrices });
    },
  });
}
